import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type AttendanceAdjustmentType = "add" | "deduct";

export type AttendanceAdjustmentRow = {
  id: string;
  ids: string[];
  employeeId: string;
  employeeName: string | null;
  employeeCode: string | null;
  adjustmentDate: string;
  adjustmentType: AttendanceAdjustmentType | "mixed";
  hasAdd: boolean;
  hasDeduct: boolean;
  isSplit: boolean;
  minutes: number | null;
  startOtMinutes: number | null;
  endOtMinutes: number | null;
  netMinutes: number;
  netStartOtMinutes: number;
  netEndOtMinutes: number;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AttendanceAdjustmentEffective = {
  minutes: number;
  startOtMinutes: number;
  endOtMinutes: number;
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapType(raw: unknown): AttendanceAdjustmentType | "mixed" {
  const v = str(raw);
  if (v === "deduct") return "deduct";
  if (v === "mixed") return "mixed";
  return "add";
}

function mapNullableMinutes(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.round(n));
}

function mapSignedMinutes(raw: unknown): number {
  if (raw == null || raw === "") return 0;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
}

function mapBool(raw: unknown, fallback: boolean): boolean {
  if (raw === true || raw === 1 || raw === "1") return true;
  if (raw === false || raw === 0 || raw === "0") return false;
  return fallback;
}

function mapRow(raw: Record<string, unknown>): AttendanceAdjustmentRow {
  const adjustmentType = mapType(raw.adjustment_type);
  const hasAdd = mapBool(raw.has_add, adjustmentType !== "deduct");
  const hasDeduct = mapBool(raw.has_deduct, adjustmentType === "deduct" || adjustmentType === "mixed");
  const netMinutes = mapSignedMinutes(raw.net_minutes ?? raw.minutes);
  const netStartOtMinutes = mapSignedMinutes(raw.net_start_ot_minutes ?? raw.start_ot_minutes);
  const netEndOtMinutes = mapSignedMinutes(raw.net_end_ot_minutes ?? raw.end_ot_minutes);
  const idsRaw = Array.isArray(raw.ids) ? raw.ids.map((id) => str(id)).filter(Boolean) : [];
  const id = str(raw.id);
  return {
    id,
    ids: idsRaw.length > 0 ? idsRaw : id ? [id] : [],
    employeeId: str(raw.employee_id),
    employeeName: raw.employee_name != null ? str(raw.employee_name) : null,
    employeeCode: raw.employee_code != null ? str(raw.employee_code) : null,
    adjustmentDate: str(raw.adjustment_date).slice(0, 10),
    adjustmentType,
    hasAdd,
    hasDeduct,
    isSplit: mapBool(raw.is_split, false),
    minutes: mapNullableMinutes(raw.minutes),
    startOtMinutes: mapNullableMinutes(raw.start_ot_minutes),
    endOtMinutes: mapNullableMinutes(raw.end_ot_minutes),
    netMinutes,
    netStartOtMinutes,
    netEndOtMinutes,
    reason: raw.reason != null ? str(raw.reason) : null,
    createdBy: raw.created_by != null ? str(raw.created_by) : null,
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

function mapEffective(raw: Record<string, unknown> | undefined): AttendanceAdjustmentEffective {
  return {
    minutes: Math.round(Number(raw?.minutes) || 0),
    startOtMinutes: Math.round(Number(raw?.start_ot_minutes) || 0),
    endOtMinutes: Math.round(Number(raw?.end_ot_minutes) || 0),
  };
}

/** Format stored minutes as e.g. "1h 30m" or "45m". */
export function formatAdjustmentMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "0m";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function formatSplitOtAdjustment(row: AttendanceAdjustmentRow): string {
  const parts: string[] = [];
  if ((row.startOtMinutes ?? 0) > 0) {
    parts.push(`Start OT ${formatAdjustmentMinutes(row.startOtMinutes ?? 0)}`);
  }
  if ((row.endOtMinutes ?? 0) > 0) {
    parts.push(`End OT ${formatAdjustmentMinutes(row.endOtMinutes ?? 0)}`);
  }
  if (parts.length === 0) {
    return formatAdjustmentMinutes(row.minutes ?? 0);
  }
  return parts.join(" · ");
}

export function formatSignedAdjustmentMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes === 0) return "0m";
  const sign = totalMinutes < 0 ? "−" : "+";
  return `${sign}${formatAdjustmentMinutes(Math.abs(totalMinutes))}`;
}

export function attendanceAdjustmentsApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export type AttendanceAdjustmentsPaginatedMeta = {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
};

type PaginatedResponse = {
  data?: Record<string, unknown>[];
  meta?: {
    current_page?: number;
    last_page?: number;
    per_page?: number;
    total?: number;
    effective?: Record<string, unknown>;
  };
};

function mapPaginatedMeta(meta: PaginatedResponse["meta"]): AttendanceAdjustmentsPaginatedMeta {
  return {
    currentPage: meta?.current_page ?? 1,
    lastPage: meta?.last_page ?? 1,
    perPage: meta?.per_page ?? 25,
    total: meta?.total ?? 0,
  };
}

/** Paginated listing (index page). Omit adjustmentDate to use list mode. */
export async function fetchAttendanceAdjustments(params: {
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
  adjustmentType?: AttendanceAdjustmentType | "";
  page?: number;
  perPage?: number;
}): Promise<{ rows: AttendanceAdjustmentRow[]; meta: AttendanceAdjustmentsPaginatedMeta }> {
  const response = await api.get<PaginatedResponse>("/api/v1/attendance/adjustments", {
    params: {
      employee_id: params.employeeId || undefined,
      date_from: params.dateFrom || undefined,
      date_to: params.dateTo || undefined,
      adjustment_type: params.adjustmentType || undefined,
      page: params.page,
      per_page: params.perPage,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRow(r)),
    meta: mapPaginatedMeta(response.data?.meta),
  };
}

export async function listAttendanceAdjustments(params: {
  employeeId: string;
  adjustmentDate: string;
}): Promise<{ rows: AttendanceAdjustmentRow[]; effective: AttendanceAdjustmentEffective }> {
  const response = await api.get<PaginatedResponse>("/api/v1/attendance/adjustments", {
    params: {
      employee_id: params.employeeId,
      adjustment_date: params.adjustmentDate,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRow(r)),
    effective: mapEffective(response.data?.meta?.effective),
  };
}

export async function createAttendanceAdjustments(payload: {
  employeeId: string;
  adjustmentDate: string;
  lines: Array<{
    adjustmentType?: AttendanceAdjustmentType;
    minutes?: number;
    startOtMinutes?: number | null;
    endOtMinutes?: number | null;
    startOtAdjustmentType?: AttendanceAdjustmentType;
    endOtAdjustmentType?: AttendanceAdjustmentType;
    reason?: string | null;
  }>;
}): Promise<{ rows: AttendanceAdjustmentRow[]; message: string; replaced: boolean }> {
  const response = await api.post<{
    message?: string;
    data?: Record<string, unknown>[];
    meta?: { replaced?: boolean };
  }>(
    `/api/v1/employees/${encodeURIComponent(payload.employeeId)}/attendance-adjustments`,
    {
      adjustment_date: payload.adjustmentDate,
      lines: payload.lines.map((line) => {
        const isSplit =
          line.startOtMinutes != null ||
          line.endOtMinutes != null ||
          line.startOtAdjustmentType != null ||
          line.endOtAdjustmentType != null;

        if (isSplit) {
          const body: Record<string, unknown> = {
            start_ot_minutes: line.startOtMinutes ?? 0,
            end_ot_minutes: line.endOtMinutes ?? 0,
            start_ot_adjustment_type: line.startOtAdjustmentType ?? line.adjustmentType ?? "add",
            end_ot_adjustment_type: line.endOtAdjustmentType ?? line.adjustmentType ?? "add",
            reason: line.reason ?? null,
          };
          if (line.adjustmentType) {
            body.adjustment_type = line.adjustmentType;
          }
          return body;
        }

        return {
          adjustment_type: line.adjustmentType ?? "add",
          minutes: line.minutes ?? 0,
          reason: line.reason ?? null,
        };
      }),
    },
  );
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    message: str(response.data?.message) || "Attendance adjustment recorded.",
    replaced: Boolean(response.data?.meta?.replaced),
  };
}

export async function deleteAttendanceAdjustment(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(
    `/api/v1/attendance/adjustments/${encodeURIComponent(id)}`,
  );
  return { message: str(response.data?.message) || "Attendance adjustment deleted." };
}
